import { useCallback, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useAnchor } from "./use-anchor";
import { useDismiss } from "./use-dismiss";
import styles from "./popovers.module.css";

/**
 * A **?** beside a heading, holding the paragraph that used to sit under it
 * (decision CS11).
 *
 * The wording is kept whole — this folds explanation away, it does not delete
 * it (UI-P1 deleted Settings' prose on request; this is different). It opens
 * on **hover and on tap**: a hover-only tip is text a touch screen can never
 * read, which would be the deletion by another route.
 */
export function HelpTip({
  label,
  children,
  testId,
}: {
  /** What the button is called for a screen reader, e.g. "About waiting on WhatsApp". */
  label: string;
  children: React.ReactNode;
  testId?: string;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const tipId = useId();
  const tip = useRef<HTMLSpanElement>(null);

  const close = useCallback((how: "outside" | "escape") => {
    setOpen(false);
    setPinned(false);
    if (how === "escape") button.current?.focus();
  }, []);
  useDismiss(open, close, wrap, tip);
  const place = useAnchor(open, button, "center");

  return (
    <span
      className={styles.help}
      ref={wrap}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => {
        // A tip opened by a click stays until dismissed; one opened by
        // hovering goes when the pointer does.
        if (!pinned) setOpen(false);
      }}
    >
      <button
        ref={button}
        type="button"
        className={styles.helpButton}
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? tipId : undefined}
        onClick={() => {
          const next = !(open && pinned);
          setOpen(next);
          setPinned(next);
        }}
        data-testid={testId}
      >
        ?
      </button>
      {/*
        Drawn at the top of the page, not inside the trigger: a panel inside a
        scrolling box is clipped by it, and one that follows its trigger while
        that box scrolls chases itself off screen (see `use-anchor.ts`).
      */}
      {open &&
        createPortal(
          <span id={tipId} ref={tip} role="tooltip" className={styles.tip} style={place}>
            {children}
          </span>,
          document.body,
        )}
    </span>
  );
}

export type RowMenuItem = {
  label: string;
  onSelect: () => void;
  /** Drawn in the danger colour and set apart by a rule — Retire, Remove. */
  danger?: boolean;
  disabled?: boolean;
  testId?: string;
};

/**
 * **⋯** — a row's secondary actions, so the row reads as its content and not
 * as a strip of buttons (decision CS10).
 *
 * Every action the row had stays in it: this moves buttons, it never drops
 * one. The items are ordinary buttons in a disclosure panel, reached with Tab,
 * and choosing one closes the panel before the action runs, so a confirmation
 * the action opens is never drawn under a menu that is still open.
 */
export function RowMenu({
  label,
  items,
  testId,
}: {
  /** e.g. "More actions for Chair 1". */
  label: string;
  items: readonly RowMenuItem[];
  testId?: string;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const panel = useRef<HTMLSpanElement>(null);

  const close = useCallback((how: "outside" | "escape") => {
    setOpen(false);
    if (how === "escape") button.current?.focus();
  }, []);
  useDismiss(open, close, wrap, panel);
  const place = useAnchor(open, button, "end");

  const plain = items.filter((item) => item.danger !== true);
  const danger = items.filter((item) => item.danger === true);

  return (
    <span className={styles.menuWrap} ref={wrap}>
      <button
        ref={button}
        type="button"
        className={styles.dots}
        aria-label={label}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((was) => !was)}
        data-testid={testId}
      >
        ⋯
      </button>
      {/* At the top of the page, for the reason given on `HelpTip`. */}
      {open &&
        createPortal(
          <span id={panelId} ref={panel} className={styles.menu} style={place}>
            {plain.map((item) => (
              <MenuButton key={item.label} item={item} onDone={() => setOpen(false)} />
            ))}
            {danger.length > 0 && plain.length > 0 && (
              <span className={styles.rule} aria-hidden="true" />
            )}
            {danger.map((item) => (
              <MenuButton key={item.label} item={item} onDone={() => setOpen(false)} />
            ))}
          </span>,
          document.body,
        )}
    </span>
  );
}

function MenuButton({
  item,
  onDone,
}: {
  item: RowMenuItem;
  onDone: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={item.danger === true ? `${styles.item} ${styles.danger}` : styles.item}
      disabled={item.disabled}
      onClick={() => {
        onDone();
        item.onSelect();
      }}
      data-testid={item.testId}
    >
      {item.label}
    </button>
  );
}
